import React, { useState, useEffect, useMemo } from 'react';
import styles from './ModifyOrderModal.module.css';

const ModifyOrderModal = ({ isOpen, onClose, order, onModify, livePrice }) => {
  const [formData, setFormData] = useState({});
  const [lots, setLots] = useState(1);

  // Use the lotSize stored in the order object itself (Source of Truth)
  const lotSize = order?.lotSize || 1;
  const quantity = useMemo(() => lots * lotSize, [lots, lotSize]);

  useEffect(() => {
    if (order) {
      setFormData({
        orderType: order.orderType,
        entryPrice: order.orderType === 'market' ? (livePrice || order.entryPrice) : (order.entryPrice || ''),
        quantity: order.quantity || '',
        targetPrice: order.targetPrice || '',
        stoplossPrice: order.stoplossPrice || '',
      });
      if (lotSize > 1) {
        setLots(order.quantity / lotSize);
      }
    }
  }, [order, lotSize, livePrice]);

  useEffect(() => {
    if (formData.orderType === 'market' && livePrice) {
      setFormData(prev => ({ ...prev, entryPrice: livePrice }));
    }
  }, [formData.orderType, livePrice]);

  if (!isOpen || !order) return null;

  const onChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLotsChange = (e) => {
    setLots(Number(e.target.value));
  };

  const handleMarketCheckbox = (e) => {
    const isChecked = e.target.checked;
    const type = isChecked ? 'market' : 'limit';
    setFormData(prev => ({
      ...prev,
      orderType: type,
      entryPrice: type === 'market' ? (livePrice || prev.entryPrice) : prev.entryPrice
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const finalQuantity = lotSize > 1 ? quantity : formData.quantity;
    onModify(order._id, { ...formData, quantity: finalQuantity });
  };

  const isMarketOrder = formData.orderType === 'market';
  const isOption = lotSize > 1;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.modalHeaderControls}>
          <div>
            <h3>Modify Order</h3>
            <p>{order.tradeSymbol}</p>
          </div>
        </div>
        <form onSubmit={handleSubmit}>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label>Price</label>
              <input
                type="number"
                name="entryPrice"
                value={isMarketOrder ? (livePrice || formData.entryPrice || '') : formData.entryPrice}
                onChange={onChange}
                disabled={isMarketOrder}
                step="0.05"
                placeholder={isMarketOrder ? 'Market' : 'Limit Price'}
              />
              <div className={styles.marketRow}>
                <input 
                  type="checkbox" 
                  id="marketExec"
                  checked={isMarketOrder}
                  onChange={handleMarketCheckbox}
                />
                <label htmlFor="marketExec">Market Execution</label>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label>{isOption ? 'Lots' : 'Quantity'}</label>
              {isOption ? (
                <div className={styles.quantityInput}>
                  <input
                    type="number"
                    value={lots}
                    onChange={handleLotsChange}
                    min="1"
                  />
                  <div className={styles.quantityDetails}>
                    <span>Lot: {lotSize}</span>
                    <span>Total: {quantity}</span>
                  </div>
                </div>
              ) : (
                <input type="number" name="quantity" value={formData.quantity} onChange={onChange} min="1" />
              )}
            </div>

            <div className={styles.formGroup}>
              <label>Target</label>
              <input type="number" name="targetPrice" value={formData.targetPrice} onChange={onChange} step="0.05" placeholder="Optional" />
            </div>

            <div className={styles.formGroup}>
              <label>Stoploss</label>
              <input type="number" name="stoplossPrice" value={formData.stoplossPrice} onChange={onChange} step="0.05" placeholder="Optional" />
            </div>
          </div>

          <div className={styles.buttonContainer}>
            <button type="button" className={styles.btnCancel} onClick={onClose}>Cancel</button>
            <button type="submit" className={styles.btnConfirm}>Update Order</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModifyOrderModal;
